// GPU フィルターのシェーダ。入力はレイヤーのストレートアルファ RGBA テクスチャ (レイヤー空間)。
// ぼかし系はプリマルチプライで計算して透明との滲みを防ぐ。

const HEAD = /* glsl */ `#version 300 es
precision highp float;
in vec2 vPos;                 // レイヤー空間 px
uniform sampler2D uSrc;
uniform vec2 uSize;           // レイヤーテクスチャのサイズ
uniform vec4 uP0;
uniform vec4 uP1;
out vec4 outColor;
vec4 fetch(vec2 p) {
  vec2 uv = p / uSize;
  if (uv.x < 0.0 || uv.y < 0.0 || uv.x > 1.0 || uv.y > 1.0) return vec4(0.0);
  vec4 c = texture(uSrc, uv);
  return vec4(c.rgb * c.a, c.a);     // プリマルチ
}
vec4 unpremul(vec4 c) { return c.a > 0.0 ? vec4(c.rgb / c.a, c.a) : vec4(0.0); }
float hash12(vec2 p) { vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
`

/** 分離ガウス / ボックス: uP0 = (dirX, dirY, radius, box?) */
export const BLUR_DIR_FRAG = HEAD + /* glsl */ `
void main() {
  float r = uP0.z;
  vec2 dir = uP0.xy;
  if (r < 0.01) { outColor = unpremul(fetch(vPos)); return; }
  bool box = uP0.w > 0.5;
  float sigma = r * 0.5;
  int taps = int(min(ceil(r), 96.0));
  vec4 sum = vec4(0.0);
  float wsum = 0.0;
  for (int i = -96; i <= 96; i++) {
    if (i < -taps || i > taps) continue;
    float x = float(i);
    float w = box ? 1.0 : exp(-x * x / (2.0 * sigma * sigma));
    sum += fetch(vPos + dir * x) * w;
    wsum += w;
  }
  outColor = unpremul(sum / wsum);
}
`

/** 移動ぼかし: uP0 = (cos, sin, distance) */
export const MOTION_FRAG = HEAD + /* glsl */ `
void main() {
  float d = uP0.z;
  vec2 dir = uP0.xy;
  int taps = int(min(ceil(d), 128.0));
  vec4 sum = vec4(0.0);
  float n = 0.0;
  for (int i = -128; i <= 128; i++) {
    if (i < -taps || i > taps) continue;
    sum += fetch(vPos + dir * (float(i) / float(max(taps, 1)) * d * 0.5));
    n += 1.0;
  }
  outColor = unpremul(sum / n);
}
`

/** 2 入力の合成: uP0.x = mode (0: unsharp, 1: highpass), uP0.y = amount, uP0.z = threshold(0..1) */
export const COMBINE_FRAG = HEAD + /* glsl */ `
uniform sampler2D uBlur;
void main() {
  vec2 uv = vPos / uSize;
  vec4 o = texture(uSrc, uv);
  vec4 b = texture(uBlur, uv);
  int mode = int(uP0.x + 0.5);
  if (mode == 0) {
    vec3 diff = o.rgb - b.rgb;
    float m = max(abs(diff.r), max(abs(diff.g), abs(diff.b)));
    if (m < uP0.z) diff = vec3(0.0);
    outColor = vec4(clamp(o.rgb + diff * uP0.y, 0.0, 1.0), o.a);
  } else {
    outColor = vec4(clamp(o.rgb - b.rgb + 0.5, 0.0, 1.0), o.a);
  }
}
`

/** 点処理: uP0.x = mode (0 invert, 1 noise, 2 pixelate, 3 emboss, 4 edges, 5 vignette, 6 sharpen, 7 desaturate) */
export const POINT_FRAG = HEAD + /* glsl */ `
float lum(vec3 c) { return dot(c, vec3(0.299, 0.587, 0.114)); }
void main() {
  int mode = int(uP0.x + 0.5);
  vec4 o = texture(uSrc, vPos / uSize);
  if (mode == 0) { outColor = vec4(1.0 - o.rgb, o.a); return; }
  if (mode == 1) {
    // ノイズ: uP0.y = 量 (0..1), uP0.z = モノクロ, uP1.x = seed
    float amt = uP0.y;
    vec2 p = floor(vPos) + uP1.x;
    vec3 n = vec3(hash12(p), hash12(p + 17.3), hash12(p + 59.1)) - 0.5;
    if (uP0.z > 0.5) n = vec3(n.x);
    outColor = vec4(clamp(o.rgb + n * amt * 2.0, 0.0, 1.0), o.a);
    return;
  }
  if (mode == 2) {
    // モザイク: uP0.y = セルサイズ
    float cs = max(1.0, uP0.y);
    vec2 cell = floor(vPos / cs) * cs + cs * 0.5;
    outColor = unpremul(fetch(cell));
    return;
  }
  if (mode == 3) {
    // エンボス: uP0.y = 角度 (rad), uP0.z = 高さ, uP0.w = 量
    vec2 d = vec2(cos(uP0.y), -sin(uP0.y)) * uP0.z;
    vec4 a = unpremul(fetch(vPos + d));
    vec4 b = unpremul(fetch(vPos - d));
    float v = (lum(b.rgb) - lum(a.rgb)) * uP0.w + 0.5;
    outColor = vec4(vec3(clamp(v, 0.0, 1.0)), o.a);
    return;
  }
  if (mode == 4) {
    // 輪郭検出 (Sobel、反転して白地に線)
    float gx = 0.0, gy = 0.0;
    for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) {
      float l = lum(unpremul(fetch(vPos + vec2(float(i), float(j)))).rgb);
      float kx = float(i) * (j == 0 ? 2.0 : 1.0);
      float ky = float(j) * (i == 0 ? 2.0 : 1.0);
      gx += l * kx; gy += l * ky;
    }
    float e = clamp(1.0 - sqrt(gx * gx + gy * gy), 0.0, 1.0);
    outColor = vec4(vec3(e), o.a);
    return;
  }
  if (mode == 5) {
    // ビネット: uP0.y = 量, uP0.z = 半径 (0..1), uP0.w = ソフトネス, uP1.xy = 中心
    vec2 c = uP1.xy;
    float d = length((vPos - c) / (uSize * 0.5));
    float v = smoothstep(uP0.z, uP0.z + max(uP0.w, 0.01), d) * uP0.y;
    outColor = vec4(o.rgb * (1.0 - v), o.a);
    return;
  }
  if (mode == 6) {
    // シャープ (3x3 ラプラシアン): uP0.y = 量
    vec4 c = fetch(vPos);
    vec4 s = fetch(vPos + vec2(1.0, 0.0)) + fetch(vPos - vec2(1.0, 0.0)) + fetch(vPos + vec2(0.0, 1.0)) + fetch(vPos - vec2(0.0, 1.0));
    vec4 r = c + (c * 4.0 - s) * uP0.y;
    outColor = vec4(clamp(unpremul(vec4(r.rgb, c.a)).rgb, 0.0, 1.0), o.a);
    return;
  }
  if (mode == 7) { float l = lum(o.rgb); outColor = vec4(vec3(l), o.a); return; }
  outColor = o;
}
`

/** 表面ぼかし (バイラテラル): uP0 = (radius, threshold 0..1) */
export const SURFACE_FRAG = HEAD + /* glsl */ `
void main() {
  float r = uP0.x;
  float th = max(uP0.y, 0.01);
  int taps = int(min(ceil(r), 12.0));
  vec4 c = unpremul(fetch(vPos));
  vec4 sum = vec4(0.0);
  float wsum = 0.0;
  for (int j = -12; j <= 12; j++) for (int i = -12; i <= 12; i++) {
    if (i < -taps || i > taps || j < -taps || j > taps) continue;
    vec4 s = unpremul(fetch(vPos + vec2(float(i), float(j))));
    float d = max(abs(s.r - c.r), max(abs(s.g - c.g), abs(s.b - c.b)));
    float w = 1.0 - smoothstep(0.0, th, d);
    sum += vec4(s.rgb * s.a, s.a) * w;
    wsum += w;
  }
  outColor = wsum > 0.0 ? unpremul(sum / wsum) : c;
}
`
